<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from '#imports';
import { useI18n } from 'vue-i18n';
import { useRouter } from '#imports';
import { useAuthState } from '~/composables/useAuthState';
import { useLocale } from '~/composables/useLocale';

// 延迟导入非关键组件
const PointsHistoryPopup = defineAsyncComponent(() => 
  import('~/components/PointsHistoryPopup.vue')
);

const router = useRouter();
const { t } = useI18n();

// 使用统一的认证状态管理
const { 
  user,
  isAuthenticated, 
  isLoggingIn, 
  handleSignIn, 
  totalPoints, 
  isLoadingAccount,
  handleLogout,
} = useAuthState();

const { getCurrentLocale, setLocale, availableLocales } = useLocale();

// 积分历史弹窗
const showPointsHistoryPopup = ref(false);

// 用户菜单相关
const showUserMenu = ref(false);
const showLanguageModal = ref(false); // 语言切换弹窗
const currentLocale = computed(() => getCurrentLocale());

// 切换语言
const changeLocale = (code: string) => {
  if (setLocale(code)) {
    showLanguageModal.value = false;
    showUserMenu.value = false;
  }
};

// 打开语言切换弹窗
const openLanguageModal = () => {
  showLanguageModal.value = true;
  // 关闭用户菜单
  showUserMenu.value = false;
};

// 关闭语言切换弹窗
const closeLanguageModal = () => {
  showLanguageModal.value = false;
};

// 处理本地登出UI
const handleLocalLogout = async () => {
  await handleLogout();
  showUserMenu.value = false;
};

// 获取用户显示名称
const displayName = computed(() => {
  if (user.value) {
    return user.value.firstName || user.value.username || 'User';
  }
  return 'User';
});

// 获取用户头像
const userAvatar = computed(() => {
  if (user.value && user.value.imageUrl) {
    return user.value.imageUrl;
  }
  return null; // 返回null将使用默认头像
});

// 滚动状态
const isScrolled = ref(false);
// 点击外部关闭用户菜单和语言弹窗
const userMenuRef = ref<HTMLElement | null>(null);
const languageModalRef = ref<HTMLElement | null>(null);

// 优化挂载处理
onMounted(() => {
  // 延迟注册不立即需要的事件监听
  setTimeout(() => {
    if (typeof window !== 'undefined') {
      // 点击外部关闭菜单和弹窗
      window.addEventListener('click', handleOutsideClick);
      // 监听滚动事件
      window.addEventListener('scroll', handleScroll);
      // 初始检查滚动状态
      handleScroll();
    }
  }, 500); // 延迟500ms注册
});

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('click', handleOutsideClick);
    window.removeEventListener('scroll', handleScroll);
  }
});

// 处理外部点击
const handleOutsideClick = (e: MouseEvent) => {
  if (userMenuRef.value && !userMenuRef.value.contains(e.target as Node) && showUserMenu.value) {
    showUserMenu.value = false;
  }
  if (languageModalRef.value && !languageModalRef.value.contains(e.target as Node) && showLanguageModal.value) {
    showLanguageModal.value = false;
  }
};

// 处理滚动事件
const handleScroll = () => {
  if (typeof window !== 'undefined') {
    isScrolled.value = window.scrollY > 10;
  }
};

// 打开积分历史弹窗
const openPointsHistoryPopup = () => {
  showPointsHistoryPopup.value = true;
  showUserMenu.value = false; // 关闭用户菜单
};

// 语言代码到国旗表情符号的映射
const getLanguageFlag = (code: string) => {
  const flagMap: Record<string, string> = {
    'en': '🇬🇧',
    'zh': '🇨🇳',
    'zh-TW': '🇹🇼', 
    'ja': '🇯🇵',
    'ko': '🇰🇷',
    'fr': '🇫🇷',
    'de': '🇩🇪',
    'es': '🇪🇸'
  };
  return flagMap[code] || '🌐';
};
</script>

<template>
  <!-- 移动端顶部导航 -->
  <div class="lg:hidden flex items-center justify-between p-4 sticky top-0 z-50 transition-all duration-300" :class="[
    isScrolled
      ? 'bg-gray-900/60 backdrop-blur-lg shadow-md'
      : 'bg-transparent'
  ]">
    <div class="flex items-center">
      <!-- 使用优化的图片组件 -->
      <OptimizedImage 
        src="/images/logo.png" 
        alt="Music Mint" 
        width="24" 
        height="24" 
        :isLazy="false"
        :isAboveTheFold="true"
        fetchPriority="high"
        imgClass="w-6 h-6"
      />
      <div class="ml-2 text-lg font-semibold" :class="isScrolled ? 'text-white' : 'text-gray-800 dark:text-gray-200'">
        MusicMint
      </div>
    </div>

    <ClientOnly>
      <!-- 未登录状态或没有 token -->
      <div v-if="!isAuthenticated">
        <button @click="handleSignIn" class="mobile-login-button" :disabled="isLoggingIn">
          <span v-if="isLoggingIn" class="mobile-loading-spinner"></span>
          {{ t('user.login') }}
        </button>
      </div>

      <!-- 已登录状态：用户信息 -->
      <div v-else class="relative" ref="userMenuRef">
        <button @click.stop="showUserMenu = !showUserMenu"
          class="flex items-center space-x-2 rounded-full p-1 hover:bg-gray-100/20 transition-colors">
          <!-- 用户头像 -->
          <div v-if="userAvatar" class="w-8 h-8 rounded-full overflow-hidden">
            <OptimizedImage 
              :src="userAvatar" 
              alt="User Avatar" 
              width="32" 
              height="32"
              imgClass="w-full h-full object-cover" 
              :isLazy="false"
            />
          </div>
          <div v-else class="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white">
            <div class="i-carbon-user text-lg"></div>
          </div>

          <!-- 用户名 (可选) -->
          <span v-if="!isScrolled" class="text-sm font-medium text-gray-800 dark:text-gray-200 hidden sm:block">
            {{ displayName }}
          </span>
        </button>

        <!-- 用户菜单 -->
        <div v-if="showUserMenu"
          class="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white/90 dark:bg-gray-800/90 backdrop-blur-md ring-1 ring-black ring-opacity-5 z-50">
          <div class="py-1">
            <!-- 用户信息 -->
            <div class="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div class="font-medium text-gray-800 dark:text-gray-200">{{ displayName }}</div>
              <div class="text-xs text-gray-500 dark:text-gray-400 truncate">
                {{ user?.primaryEmailAddress?.emailAddress || '' }}
              </div>
              <div
                class="mt-1 text-xs text-blue-500 dark:text-blue-400 flex items-center cursor-pointer hover:text-blue-600 dark:hover:text-blue-300"
                @click="openPointsHistoryPopup">
                <div class="i-carbon-money mr-1"></div>
                {{ isLoadingAccount ? t('common.loading') : `${t('user.points')}: ${totalPoints}` }}
                <span class="pl-4 cursor-pointer underline text-blue-500 dark:text-blue-400">{{ t('common.view') }}</span>
              </div>
            </div>

            <!-- 页脚链接 -->
            <div
              class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200 border-b border-gray-200 dark:border-gray-700">
              <a href="/privacy.html" target="_blank"
                class="block py-1.5 hover:text-blue-500 dark:hover:text-blue-400 transition-colors flex items-center">
                <div class="i-carbon-document-security mr-2 text-lg"></div>
                {{ t('footer.privacyPolicy') }}
              </a>
              <a href="/terms.html" target="_blank"
                class="block py-1.5 hover:text-blue-500 dark:hover:text-blue-400 transition-colors flex items-center">
                <div class="i-carbon-document-signed mr-2 text-lg"></div>
                {{ t('footer.termsOfService') }}
              </a>
              <a href="#"
                class="block py-1.5 hover:text-blue-500 dark:hover:text-blue-400 transition-colors flex items-center"
                @click.prevent="router.push('/about'); showUserMenu = false;">
                <div class="i-carbon-information mr-2 text-lg"></div>
                {{ t('footer.aboutUs') }}
              </a>
            </div>

            <!-- 操作选项 -->
            <div class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
              <a href="#"
                class="block py-1.5 hover:text-blue-500 dark:hover:text-blue-400 transition-colors flex items-center"
                @click.prevent="openLanguageModal">
                <div class="i-carbon-translate mr-2 text-lg"></div>
                {{ t('common.language') }}
              </a>
              <a href="#"
                class="block py-1.5 text-red-500 hover:text-red-600 transition-colors flex items-center"
                @click.prevent="handleLocalLogout">
                <div class="i-carbon-logout mr-2 text-lg"></div>
                {{ t('user.logout') }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </ClientOnly>
  </div>

  <!-- 语言切换弹窗 -->
  <Teleport to="body">
    <div v-if="showLanguageModal" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div class="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6" ref="languageModalRef">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">{{ t('common.selectLanguage') }}</h3>
          <button @click="closeLanguageModal"
            class="text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-200">
            <div class="i-carbon-close text-xl"></div>
          </button>
        </div>
        <div class="grid grid-cols-1 gap-2">
          <button v-for="locale in availableLocales" :key="locale.code"
            @click="changeLocale(locale.code)"
            class="flex items-center justify-between p-3 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            :class="{ 'bg-blue-50 dark:bg-blue-900/30': currentLocale === locale.code }">
            <div class="flex items-center">
              <div class="w-6 h-6 mr-3">{{ getLanguageFlag(locale.code) }}</div>
              <span>{{ locale.name }}</span>
            </div>
            <div v-if="currentLocale === locale.code" class="text-blue-500 dark:text-blue-400">
              <div class="i-carbon-checkmark text-lg"></div>
            </div>
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- 积分历史弹窗 -->
  <PointsHistoryPopup v-model:visible="showPointsHistoryPopup" />
</template>

<style scoped>
/* 确保导航栏在滚动时平滑过渡 */
.sticky {
  position: fixed;
  width: 100%;
  left: 0;
  top: 0;
}

/* 增加导航栏的高度和美观度 */
.lg\:hidden {
  height: 64px;
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}

/* 增强磨砂玻璃效果 */
.backdrop-blur-lg {
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
}

/* 移动端登录按钮样式 */
.mobile-login-button {
  @apply px-4 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-full text-sm font-medium transition-colors;
}

/* 加载动画 */
.mobile-loading-spinner {
  @apply inline-block w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin;
}

/* 语言切换弹窗从右侧滑入动画 */
.language-modal-slide-in {
  animation: slide-in 0.3s ease-out forwards;
}

@keyframes slide-in {
  from {
    transform: translateX(100%);
  }

  to {
    transform: translateX(0);
  }
}
</style>